import { CacheKey } from './constants';

const RETAINED_KEYS: string[] = [
  // Preferences describing the browser rather than the reader
  CacheKey.ThemeMode,
  CacheKey.ExamContentsSidebarSwitch,
  CacheKey.QuestionTreeSidebarSwitch,
  CacheKey.ExamViewerPanelSize,
  // Clearing this would let a page that keeps failing reload without end
  CacheKey.ChunkReloaded,
  // The bundled PDF viewer's own zoom, scroll and sidebar settings
  'pdfjs.preferences',
];

const RETAINED_PREFIXES: string[] = [
  // Side panel widths are one key per panel, namespaced under a shared prefix
  `${CacheKey.SidePanelWidth}:`,
  // Amplify's sign-in challenge, which it reads back only when a document loads
  // part-way through one, and expires itself after three minutes
  'CognitoSignInState.',
];

const isRetained = (key: string) =>
  RETAINED_KEYS.includes(key) || RETAINED_PREFIXES.some(prefix => key.startsWith(prefix));

/** One store or key a browser refuses must not stop the others being cleared. */
const attempt = (clear: () => void) => {
  try {
    clear();
  } catch {
    // Nothing to recover: the value the browser kept is the value it refused
  }
};

const clearStore = (store: Storage) => {
  // Snapshot the keys first: removing while enumerating would skip entries
  Object.keys(store)
    .filter(key => !isRetained(key))
    .forEach(key => attempt(() => store.removeItem(key)));
};

/**
 * Drops what this origin's local storage and this tab's session storage hold for
 * a reader who has gone — their cached identity, the filters they had set, and
 * Amplify's stored tokens. Only the keys above are named, so a key stored later
 * is forgotten by default.
 *
 * Amplify clears its own tokens too, but only on the paths it recognises: a
 * token it cannot decode leaves them in place, and device metadata it only
 * removes on `forgetDevice`. Sweeping them costs nothing, since this runs when
 * the session is already gone.
 *
 * The IndexedDB store of annotated exam scripts is left alone deliberately:
 * until a marker uploads, it holds the only copy of their annotations.
 */
const clearStoredUserData = (): void => {
  attempt(() => clearStore(localStorage));
  attempt(() => clearStore(sessionStorage));
};

export default clearStoredUserData;
